import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomButton from "../CustomButton";
import CustomTextField from "../CustomTextField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";

export interface InvoiceItem {
  id: number;
  service: number | string;
  serviceId?: string;
  serviceLabel?: string;
  quantity: number;
  unitCost: number;
  discount: number;
  totalPrice: number;
}

interface InvoiceItemsEditorProps {
  value: InvoiceItem[] | undefined;
  onChange: (items: InvoiceItem[]) => void;
  vatValue?: number | string;
  onVatChange?: (value: number) => void;
  discountValue?: number | string;
  onDiscountChange?: (value: number) => void;
  isEditMode?: boolean;
  servicesList?: any[];
}

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));

const computeItemTotal = (
  item: Pick<InvoiceItem, "quantity" | "unitCost" | "discount">
) => {
  const qty = Number(item.quantity) || 0;
  const unit = Number(item.unitCost) || 0;
  const discountPercent = clampPercentage(Number(item.discount) || 0);
  const subtotal = qty * unit;
  const discountAmount = (subtotal * discountPercent) / 100;
  return Math.round((subtotal - discountAmount) * 100) / 100;
};

const parseServiceTitleValue = (title: unknown) => {
  const match = String(title ?? "").match(/\d+/);
  return match ? Number(match[0]) : NaN;
};

const resolveServiceId = (service: any): string | undefined => {
  const id = service?.id ?? service?._id ?? service?.service_id;
  if (id === undefined || id === null) return undefined;
  const text = String(id).trim();
  return text ? text : undefined;
};

const resolveServicePrice = (service: any): number => {
  const raw =
    service?.price ??
    service?.serviceprice ??
    service?.servicePrice ??
    service?.price_discount;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : 0;
};

const InvoiceItemsEditor: React.FC<InvoiceItemsEditorProps> = ({
  value,
  onChange,
  vatValue = 0,
  onVatChange,
  discountValue = 0,
  onDiscountChange,
  isEditMode = false,
  servicesList = [],
}) => {
  const ensureItemArray = (incoming: any): InvoiceItem[] =>
    Array.isArray(incoming) ? incoming : [];

  const [localItems, setLocalItems] = React.useState<InvoiceItem[]>(
    ensureItemArray(value)
  );

  React.useEffect(() => {
    setLocalItems(ensureItemArray(value));
  }, [value]);

  const updateItems = (newItems: InvoiceItem[]) => {
    setLocalItems(newItems);
    onChange(newItems);
  };

  const handleAddItem = () => {
    const nextId = localItems.length
      ? Math.max(...localItems.map((it) => it.id)) + 1
      : 1;

    const firstService =
      Array.isArray(servicesList) && servicesList.length > 0
        ? servicesList[0]
        : null;
    const firstServiceId = firstService ? resolveServiceId(firstService) : "";
    const parsedTitle = firstService
      ? parseServiceTitleValue(firstService.title)
      : NaN;

    const fallbackServiceValue =
      firstServiceId || (Number.isNaN(parsedTitle) ? 30 : parsedTitle);
    const fallbackUnitCost = firstService ? resolveServicePrice(firstService) : 0;

    const newItem: InvoiceItem = {
      id: nextId,
      service: fallbackServiceValue,
      serviceId: firstServiceId || undefined,
      quantity: 1,
      unitCost: fallbackUnitCost,
      discount: 0,
      totalPrice: computeItemTotal({
        quantity: 1,
        unitCost: fallbackUnitCost,
        discount: 0,
      }),
    };

    updateItems([...localItems, newItem]);
  };

  const totalAmount = localItems.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  );
  const discountPercent = Number(discountValue) || 0;
  const vatPercent = Number(vatValue) || 0;
  const discountAmount = (totalAmount * discountPercent) / 100;
  const amountAfterDiscount = totalAmount - discountAmount;
  const vatAmount = (amountAfterDiscount * vatPercent) / 100;
  const netAmount = Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <CustomButton
          textColor={COLORS.WhiteText}
          label=""
          bgColor={COLORS.MainColor}
          iconButtonSize={40}
          startIcon={<AddIcon />}
          onClick={handleAddItem}
        />
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Unit Cost</TableCell>
              <TableCell>Discount (%)</TableCell>
              <TableCell>Total Price</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {localItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  {(() => {
                    // Create combined service options
                    const serviceOptions: Array<{
                      value: number | string;
                      label: string;
                      price?: number;
                      id?: string;
                    }> = [];

                    // Add all services from servicesList
                    if (servicesList && servicesList.length > 0) {
                      servicesList.forEach((service) => {
                        const id = resolveServiceId(service);
                        const numericTitle = parseServiceTitleValue(
                          service.title
                        );
                        const value =
                          id || (!Number.isNaN(numericTitle) ? numericTitle : undefined);
                        if (value === undefined) return;
                        const price = resolveServicePrice(service);
                        const title =
                          service.title ||
                          service.name ||
                          service.service_name ||
                          String(value);
                        const label = Number.isFinite(price)
                          ? `${title} - $${price}`
                          : String(title);
                        serviceOptions.push({
                          value,
                          label,
                          price,
                          id: id || undefined,
                        });
                      });
                    } else {
                      // Default options
                      serviceOptions.push(
                        { value: 60, label: "60", price: 20 },
                        { value: 90, label: "90", price: 30 },
                        { value: 120, label: "120", price: 40 }
                      );
                    }

                    // Add any missing services from current items
                    localItems.forEach((it) => {
                      const serviceValue =
                        it.service ?? it.serviceId ?? undefined;
                      if (
                        serviceValue === undefined ||
                        serviceValue === null ||
                        String(serviceValue).trim() === ""
                      ) {
                        return;
                      }
                      const exists = serviceOptions.some(
                        (opt) => String(opt.value) === String(serviceValue)
                      );
                      if (!exists) {
                        serviceOptions.push({
                          value: serviceValue,
                          label: it.serviceLabel || String(serviceValue),
                          price: it.unitCost || 0,
                          id:
                            it.serviceId ||
                            (typeof serviceValue === "string"
                              ? serviceValue
                              : undefined),
                        });
                      }
                    });

                    return (
                      <Select
                        size="small"
                        value={item.service ?? ""}
                        onChange={(e) => {
                          const newService = e.target.value as string | number;
                          const updatedItems = localItems.map((it) => {
                            if (it.id !== item.id) return it;
                            const updated: InvoiceItem = {
                              ...it,
                              service: newService,
                            };
                            const selectedOption = serviceOptions.find(
                              (opt) =>
                                String(opt.value) === String(newService)
                            );

                            if (selectedOption) {
                              const resolvedId =
                                selectedOption.id ||
                                (typeof selectedOption.value === "string"
                                  ? selectedOption.value
                                  : undefined);
                              if (resolvedId) {
                                updated.serviceId = resolvedId;
                              }
                              updated.quantity = 1;
                              updated.unitCost = selectedOption.price || 0;
                              updated.discount = 0;
                              updated.totalPrice = computeItemTotal(updated);
                              return updated;
                            }

                            // Fallback for default values
                            const numericService = Number(newService);
                            if (numericService === 60) {
                              updated.quantity = 1;
                              updated.unitCost = 20;
                              updated.discount = 0;
                            }
                            if (numericService === 90) {
                              updated.quantity = 1;
                              updated.unitCost = 30;
                              updated.discount = 0;
                            }
                            if (numericService === 120) {
                              updated.quantity = 1;
                              updated.unitCost = 40;
                              updated.discount = 0;
                            }
                            updated.totalPrice = computeItemTotal(updated);
                            return updated;
                          });
                          updateItems(updatedItems);
                        }}
                        sx={{ minWidth: 150 }}
                      >
                        {serviceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.quantity}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) => {
                        if (it.id !== item.id) return it;
                        const updated = {
                          ...it,
                          quantity: value,
                        } as InvoiceItem;
                        updated.totalPrice = computeItemTotal(updated);
                        return updated;
                      });
                      updateItems(updatedItems);
                    }}
                    fullWidth
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.unitCost}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) => {
                        if (it.id !== item.id) return it;
                        const updated = {
                          ...it,
                          unitCost: value,
                        } as InvoiceItem;

                        const selectedService = servicesList.find(
                          (s) => resolveServicePrice(s) === value
                        );

                        if (selectedService) {
                          const resolvedId = resolveServiceId(selectedService);
                          if (resolvedId) {
                            updated.service = resolvedId;
                            updated.serviceId = resolvedId;
                          } else {
                            const parsedTitle = parseServiceTitleValue(
                              selectedService.title
                            );
                            if (!Number.isNaN(parsedTitle)) {
                              updated.service = parsedTitle;
                            }
                          }
                          updated.serviceLabel =
                            selectedService.title ||
                            selectedService.name ||
                            updated.serviceLabel;
                        }

                        updated.totalPrice = computeItemTotal(updated);
                        return updated;
                      });
                      updateItems(updatedItems);
                    }}
                    fullWidth
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.discount}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) => {
                        if (it.id !== item.id) return it;
                        const updated = {
                          ...it,
                          discount: clampPercentage(value),
                        } as InvoiceItem;
                        updated.totalPrice = computeItemTotal(updated);
                        return updated;
                      });
                      updateItems(updatedItems);
                    }}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.01, min: 0, max: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <CustomTextField
                    value={item.totalPrice}
                    type="number"
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const updatedItems = localItems.map((it) =>
                        it.id === item.id ? { ...it, totalPrice: value } : it
                      );
                      updateItems(updatedItems);
                    }}
                    fullWidth
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      const updatedItems = localItems.filter(
                        (it) => it.id !== item.id
                      );
                      updateItems(updatedItems);
                    }}
                    sx={{ minWidth: 0, padding: 0 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: { xs: "100%", sm: 360 },
            ml: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <CustomLabel text="Subtotal" />
            <CustomLabel
              text={`${totalAmount.toFixed(2)}$`}
              fontWeight="bold"
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="Discount" />
            <CustomLabel text="%" />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditMode ? (
                <>
                  <CustomTextField
                    value={discountPercent}
                    type="number"
                    onChange={(e) =>
                      onDiscountChange?.(
                        clampPercentage(Number(e.target.value) || 0)
                      )
                    }
                    size="small"
                    sx={{ width: 80 }}
                    inputProps={{ step: 0.01, min: 0, max: 100 }}
                  />
                </>
              ) : (
                <CustomLabel text={`${discountPercent}%`} />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CustomLabel text="VAT" />
            <CustomLabel text="%" />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEditMode ? (
                <>
                  <CustomTextField
                    value={vatPercent}
                    type="number"
                    onChange={(e) =>
                      onVatChange?.(
                        clampPercentage(Number(e.target.value) || 0)
                      )
                    }
                    size="small"
                    sx={{ width: 80 }}
                    inputProps={{ step: 0.01, min: 0, max: 100 }}
                  />
                </>
              ) : (
                <CustomLabel text={`${vatPercent}%`} />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #e0e0e0",
              pt: 1,
              mt: 1,
            }}
          >
            <CustomLabel text="Net Amount" fontWeight="bold" />
            <CustomLabel text={`${netAmount.toFixed(2)}$`} fontWeight="bold" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceItemsEditor;
